/* eslint-disable camelcase */
/** @jsxImportSource @emotion/react */
import { memo, useState } from "react";
import { Controller, useFieldArray, useFormContext, useWatch, ArrayField } from "react-hook-form";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";

import { Grid, Typography } from "@material-ui/core";

import {
  Group,
  Label,
  Icon,
  Checkbox,
  RadioGroup,
  Button,
  Select,
  ImageUploader,
  WatermarkPreviewBlock,
  AllowedDomainModal,
} from "..";
import { useAccount } from "../../context/account";
import { ActivityMode, Tier, WatermarkPosition, WatermarkType } from "../../types";
import { validImage } from "../../utils/validators";

import {
  select,
  menu,
  watermarkImgUploader,
  container,
  allowedDomainContent,
  addIcon,
  btnAddDomain,
  hidden,
} from "./styles";

export interface ContentProtectionValues {
  enableWatermark: boolean;
  watermarkType: WatermarkType;
  watermarkPosition: WatermarkPosition;
  domainControl: boolean;
  watermarkImg: FileList;
  restrictionDomains: Array<Partial<ArrayField<Record<string, any>, "id" | "domain">>>;
}

interface Props {
  className?: string;
  editable: boolean;
  setWatermarkImageTouched: React.Dispatch<React.SetStateAction<boolean>>;
  imageUrl: string;
  isEventAfterLive: boolean;
  isVodToLive?: boolean;
}

const ContentProtectionGroup = ({
  className,
  editable,
  setWatermarkImageTouched,
  imageUrl,
  isEventAfterLive,
  isVodToLive = false,
}: Props): JSX.Element => {
  const intl = useIntl();
  const { tier } = useAccount();
  const { id } = useParams<{ id?: string }>();
  const { register, control, errors, clearErrors, getValues } = useFormContext();

  // If use optional rendering to register a field which didn't render at first time, the field can't be reset with data and would be defaultValue
  const { enableWatermark, watermarkType, watermarkPosition, domainControl } = useWatch<ContentProtectionValues>({
    control,
    name: ["enableWatermark", "watermarkType", "watermarkPosition", "domainControl"],
    defaultValue: {
      enableWatermark: false,
      watermarkType: WatermarkType.IMAGE,
      watermarkPosition: WatermarkPosition.BOTTOM_RIGHT,
      domainControl: false,
    },
  });

  const [watermarkUrl, setWatermarkUrl] = useState(imageUrl);
  const [showAllowedDomainModal, setShowAllowedDomainModal] = useState<boolean>(false);

  const isEnterprise = tier === Tier.ENTERPRISE;
  const { type: eventType, ultraLowLatency, activityMode } = getValues(["type", "ultraLowLatency", "activityMode"]);

  const { fields, prepend, remove } = useFieldArray({
    control,
    name: "restrictionDomains",
  });

  return (
    <>
      <Group
        className={className}
        title={intl.formatMessage({
          id: "event-detail.content-protection-settings",
        })}
      >
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Label
              text={intl.formatMessage({
                id: "event-detail.content-protection",
              })}
              tip={intl.formatMessage({
                id: isVodToLive ? "vod-to-live-page.content-protection.tip" : "event-detail.content-protection.tip",
              })}
            />
            <Controller
              render={({ value, onChange, ref, ...rest }): JSX.Element => (
                <Checkbox
                  label={intl.formatMessage({
                    id: "event-detail.drm.label",
                  })}
                  checked={value}
                  onChange={(e): void => onChange(e.target.checked)}
                  disabled={!!id || ultraLowLatency || activityMode === ActivityMode.PlaybackMode}
                  {...rest}
                />
              )}
              name="drm"
              control={control}
            />
            {isEnterprise && (
              <>
                <Controller
                  render={({ value, onChange, ref, ...rest }): JSX.Element => (
                    <Checkbox
                      label={intl.formatMessage({
                        id: "event-detail.enable-watermark",
                      })}
                      checked={value}
                      onChange={(e): void => onChange(e.target.checked)}
                      disabled={!editable || isEventAfterLive}
                      {...rest}
                    />
                  )}
                  name="enableWatermark"
                  control={control}
                  defaultValue={enableWatermark}
                />
                <Controller
                  render={({ value, onChange, ref, ...rest }): JSX.Element => (
                    <Checkbox
                      label={intl.formatMessage({
                        id: "event-detail.enable-domain-control",
                      })}
                      checked={value}
                      onChange={(e): void => onChange(e.target.checked)}
                      disabled={!editable || isEventAfterLive}
                      {...rest}
                    />
                  )}
                  name="domainControl"
                  control={control}
                  defaultValue={domainControl}
                />
              </>
            )}
          </Grid>

          {isEnterprise && (
            <Grid container item spacing={3} css={!enableWatermark && hidden}>
              <Grid item xs={6}>
                <Controller
                  render={({ value, onChange, ref, ...rest }): JSX.Element => (
                    <RadioGroup
                      label={intl.formatMessage({
                        id: "event-detail.type-of-watermark",
                      })}
                      tip={intl.formatMessage({
                        id: "event-detail.type-of-watermark.tip",
                      })}
                      radios={[
                        {
                          label: intl.formatMessage({
                            id: "event-detail.watermark.image",
                          }),
                          value: WatermarkType.IMAGE,
                        },
                        {
                          label: intl.formatMessage({
                            id: "event-detail.watermark.user-id",
                          }),
                          value: WatermarkType.USER_ID,
                          disabled: eventType === "PUBLIC",
                        },
                      ]}
                      onChange={(e): void => {
                        onChange(e);
                        if (value === WatermarkType.USER_ID) {
                          setWatermarkUrl("");
                        }
                      }}
                      disabled={!editable || isEventAfterLive}
                      {...rest}
                      value={value}
                    />
                  )}
                  name="watermarkType"
                  control={control}
                  defaultValue={watermarkType}
                />
              </Grid>
              <Grid item xs={6}>
                <Label
                  text={intl.formatMessage({
                    id: "evenet-detail.watermark-position",
                  })}
                />
                <Controller
                  render={({ value, ref, ...rest }): JSX.Element => (
                    <Select
                      displayEmpty
                      value={value}
                      options={[
                        {
                          value: WatermarkPosition.BOTTOM_RIGHT,
                          label: intl.formatMessage({
                            id: "evenet-detail.watermark-position.bottom-right",
                          }),
                        },
                        {
                          value: WatermarkPosition.BOTTOM_LEFT,
                          label: intl.formatMessage({
                            id: "evenet-detail.watermark-position.bottom-left",
                          }),
                        },
                        {
                          value: WatermarkPosition.UPPER_RIGHT,
                          label: intl.formatMessage({
                            id: "evenet-detail.watermark-position.upper-right",
                          }),
                        },
                        {
                          value: WatermarkPosition.UPPER_LEFT,
                          label: intl.formatMessage({
                            id: "evenet-detail.watermark-position.upper-left",
                          }),
                        },
                        {
                          value: WatermarkPosition.RANDOM,
                          label: intl.formatMessage({
                            id: "evenet-detail.watermark-position.random",
                          }),
                        },
                      ]}
                      customizeStyle={{
                        select,
                        menu,
                      }}
                      disabled={!editable || isEventAfterLive}
                      {...rest}
                    />
                  )}
                  name="watermarkPosition"
                  control={control}
                  defaultValue={watermarkPosition}
                />
              </Grid>
              {watermarkType === WatermarkType.IMAGE && (
                <Grid item xs={6}>
                  <Label
                    text={intl.formatMessage({
                      id: "event-detail.upload-watermark-image.label",
                    })}
                    tip={intl.formatMessage({
                      id: "event-detail.upload-watermark-image.tip",
                    })}
                  />
                  <div css={watermarkImgUploader}>
                    <ImageUploader
                      name="watermarkImg"
                      value={watermarkUrl}
                      accept="image/png, image/jpeg, image/gif"
                      onChange={(url): void => {
                        setWatermarkUrl(url);
                        setWatermarkImageTouched(true);
                        if (!url) clearErrors("watermarkImg");
                      }}
                      inputRef={register({
                        validate: (files) => validImage(files, 1, ["jpe?g", "png", "gif"]),
                        required: enableWatermark && watermarkType === WatermarkType.IMAGE && !watermarkUrl,
                      })}
                      helperText={
                        errors.watermarkImg &&
                        intl.formatMessage({
                          id:
                            errors.watermarkImg.type === "required"
                              ? "validation.required"
                              : "validation.invalid-format",
                        })
                      }
                      customizeStyle={{
                        container: container(!editable || isEventAfterLive),
                      }}
                      disabled={!editable || isEventAfterLive}
                    />
                  </div>
                </Grid>
              )}
              <Grid item xs={6}>
                <Label
                  text={intl.formatMessage({
                    id: "evenet-detail.watermark.preview",
                  })}
                />
                <WatermarkPreviewBlock
                  type={watermarkType as string}
                  position={watermarkPosition as string}
                  watermarkUrl={watermarkUrl}
                />
              </Grid>
            </Grid>
          )}
          {isEnterprise && domainControl && (
            <Grid item container spacing={3}>
              <Grid item xs={12}>
                <Label
                  text={intl.formatMessage({
                    id: "event-detail.allowed-domain",
                  })}
                  tip={intl.formatMessage({
                    id: "event-detail.allowed-domain.tip",
                  })}
                />
                <Typography variant="body2" css={allowedDomainContent}>
                  {fields.length > 0
                    ? intl.formatMessage(
                        {
                          id: "event-detail.allowed-domain.content",
                        },
                        { count: fields.length }
                      )
                    : intl.formatMessage({
                        id: "event-detail.allowed-domain.content.empty",
                      })}
                </Typography>
                <Button
                  css={btnAddDomain}
                  variant="text"
                  color="primary"
                  onClick={() => setShowAllowedDomainModal(true)}
                >
                  <Icon name="add" css={addIcon} fontSize="inherit" />
                  {intl.formatMessage(
                    {
                      id: "event-detail.btn.add-domain",
                    },
                    { count: fields.length }
                  )}
                </Button>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Group>
      <AllowedDomainModal
        open={showAllowedDomainModal}
        onClose={() => setShowAllowedDomainModal(false)}
        fields={fields}
        onAdd={(domain: string) => prepend({ domain })}
        onRemove={(index: number) => remove(index)}
        register={register}
        editable={editable && !isEventAfterLive}
      />
    </>
  );
};

export default memo(ContentProtectionGroup);
